import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useSnackbar from '../../hooks/useSnackbar';
import {
  logQuoteSubmission,
  logQuoteCancellation,
  logQuoteEdit,
  logQuotingAnalytics,
  logQuoteCounter,
  logQuoteDecline,
  logQuoteDiscard,
  logQuoteAccept,
} from './quotingAnalytics';
import {
  cancelQuote,
  getQuotes,
  submitQuote,
  counterQuote,
  declineQuote,
  discardQuote,
  acceptQuote,
} from './quotesHttp';
import { isMyLaneView } from '../../utilities/viewUtils';
import { useTranslation } from 'react-i18next';

export const useQuoteData = () => {
  const queryClient = useQueryClient();
  const { showSnackbar, showDriftSnackbar, buttonOnClick } = useSnackbar();
  const { t } = useTranslation();

  const quotesResult = useQuery({
    queryKey: ['quotes'],
    queryFn: () => getQuotes(),
    staleTime: 1000,
    refetchOnMount: false,
  });

  const showSnackbarOnError = () =>
    showDriftSnackbar({
      message: t('Quoting Error'),
      variant: 'error',
      style: {
        width: '374px',
      },
      button: { label: 'Dismiss', onClick: buttonOnClick },
      autoHideDuration: 9999999,
      slotProps: {
        content: {
          sx: {
            '& .MuiSnackbarContent-message': {
              width: '303px !important',
            },
          },
        },
      },
    });

  const { mutateAsync: submitQuoteMutation, isPending: isLoadingSubmitQuote } =
    useMutation({
      mutationFn: submitQuote,
      onSuccess: (data, { orderId }) => {
        if (quotesResult.data) {
          const foundQuote = quotesResult.data.find(
            (x) => x.orderId === orderId
          );

          const newData = foundQuote
            ? quotesResult.data.map((x) =>
                x.orderId === orderId ? { ...x, ...data } : x
              )
            : [...quotesResult.data, data];

          queryClient.setQueryData(['quotes'], newData);
          showSnackbar({
            message: t(`quote_sent_contact`),
            type: 'info',
          });
        }
      },
      onError: () => {
        showSnackbarOnError();
      },
    });

  const { mutateAsync: cancelQuoteMutation, isPending: isLoadingCancelQuote } =
    useMutation({
      mutationFn: cancelQuote,
      onSuccess: (_data, { orderId }) => {
        if (quotesResult.data) {
          const newData = quotesResult.data.filter(
            (x) => x.orderId !== orderId
          );

          queryClient.setQueryData(['quotes'], newData);
          showSnackbar({
            message: t(`quote_cancelled`),
            type: 'info',
          });
        }
      },
      onError: () => {
        showSnackbarOnError();
      },
    });

  const {
    mutateAsync: counterQuoteMutation,
    isPending: isLoadingCounterQuote,
  } = useMutation({
    mutationFn: counterQuote,
    onSuccess: (data, { orderId }) => {
      if (quotesResult.data) {
        const foundQuote = quotesResult.data.find((x) => x.orderId === orderId);

        const newData = foundQuote
          ? quotesResult.data.map((x) =>
              x.orderId === orderId ? { ...x, ...data } : x
            )
          : [...quotesResult.data, data];

        queryClient.setQueryData(['quotes'], newData);
        showSnackbar({
          message: `Counter sent, we'll reach out shortly.`,
          type: 'info',
        });
      }
    },
    onError: () => {
      showSnackbarOnError();
    },
  });

  const {
    mutateAsync: declineQuoteMutation,
    isPending: isLoadingDeclineQuote,
  } = useMutation({
    mutationFn: declineQuote,
    onSuccess: (data, { orderId }) => {
      if (quotesResult.data) {
        const foundQuote = quotesResult.data.find((x) => x.orderId === orderId);

        const newData = foundQuote
          ? quotesResult.data.map((x) =>
              x.orderId === orderId ? { ...x, ...data } : x
            )
          : [...quotesResult.data, data];
        queryClient.setQueryData(['quotes'], newData);
        showSnackbar({
          message: `Quote has been declined.`,
          type: 'info',
        });
      }
    },
    onError: () => {
      showSnackbarOnError();
    },
  });

  const {
    mutateAsync: discardQuoteMutation,
    isPending: isLoadingDiscardQuote,
  } = useMutation({
    mutationFn: discardQuote,
    onSuccess: (_data, { orderId }) => {
      if (quotesResult.data) {
        const newData = quotesResult.data.filter((x) => x.orderId !== orderId);

        queryClient.setQueryData(['quotes'], newData);
        showSnackbar({
          message: `Quote discarded.`,
          type: 'info',
        });
      }
    },
    onError: () => {
      showSnackbarOnError();
    },
  });

  const {
    mutateAsync: acceptQuoteMutation,
    isPending: isLoadingAcceptedQuote,
  } = useMutation({
    mutationFn: acceptQuote,
    onSuccess: (data, { orderId }) => {
      if (quotesResult.data) {
        const foundQuote = quotesResult.data.find((x) => x.orderId === orderId);

        const newData = foundQuote
          ? quotesResult.data.map((x) =>
              x.orderId === orderId ? { ...x, ...data } : x
            )
          : [...quotesResult.data, data];
        queryClient.setQueryData(['quotes'], newData);
        showSnackbar({
          message: t('quote_accepted'),
          type: 'info',
        });
      }
    },
    onError: () => {
      showSnackbarOnError();
    },
  });

  return {
    ...quotesResult,
    isSubmitting: isLoadingSubmitQuote,
    isCancelling: isLoadingCancelQuote,
    isCountering: isLoadingCounterQuote,
    isDeclining: isLoadingDeclineQuote,
    isDiscarding: isLoadingDiscardQuote,
    isAccepting: isLoadingAcceptedQuote,
    submitQuote: async (amount: number, orderId: string, isEdit: boolean) => {
      await submitQuoteMutation({ amount, orderId });
      if (isEdit && !isMyLaneView()) {
        logQuotingAnalytics(logQuoteEdit(amount, orderId));
        return;
      }
      if (!isMyLaneView()) {
        logQuotingAnalytics(logQuoteSubmission(amount, orderId));
      }
    },
    cancelQuote: async (orderId: string) => {
      await cancelQuoteMutation({ orderId });
      if (!isMyLaneView()) {
        logQuotingAnalytics(logQuoteCancellation(orderId));
      }
    },
    getQuoteByOrderId: (orderId?: string) => {
      return quotesResult.data?.find((x) => x.orderId === orderId);
    },
    counterQuote: async (amount: number, orderId: string) => {
      await counterQuoteMutation({ amount, orderId });
      if (!isMyLaneView()) {
        logQuotingAnalytics(logQuoteCounter(amount, orderId));
      }
    },
    discardQuote: async (orderId: string) => {
      await discardQuoteMutation({ orderId });
      const quote = quotesResult.data?.find((x) => x.orderId === orderId);
      if (!isMyLaneView()) {
        logQuotingAnalytics(logQuoteDiscard(orderId, quote?.amount));
      }
    },
    declineQuote: async (orderId: string) => {
      await declineQuoteMutation({ orderId });
      const quote = quotesResult.data?.find((x) => x.orderId === orderId);
      if (!isMyLaneView()) {
        logQuotingAnalytics(logQuoteDecline(orderId, quote?.amount));
      }
    },
    acceptQuote: async (orderId: string) => {
      await acceptQuoteMutation({ orderId });
      const quote = quotesResult.data?.find((x) => x.orderId === orderId);
      if (!isMyLaneView()) {
        logQuotingAnalytics(logQuoteAccept(orderId, quote?.amount));
      }
    },
  };
};

export const useQuoteDataById = (orderId?: string) => {
  const {
    getQuoteByOrderId,
    submitQuote,
    cancelQuote,
    discardQuote,
    declineQuote,
    counterQuote,
    acceptQuote,
    isSubmitting,
    isCancelling,
  } = useQuoteData();

  const quote = getQuoteByOrderId(orderId);

  return {
    ...quote,
    canEdit: quote?.canCounter,
    hasQuote: quote !== undefined,
    submitQuote,
    cancelQuote,
    discardQuote,
    declineQuote,
    counterQuote,
    acceptQuote,
    isSubmitting,
    isCancelling,
  };
};

export default useQuoteData;
